import { useCurrentUserPermissions } from "@appretio/shared";
import { Sheet } from "@appretio/ui-kit";
import { NavLink, Outlet } from "react-router-dom";
import {
  content,
  group,
  groupLabel,
  item,
  layout,
  nav,
  title
} from "./SideNavLayout.css";

/** Handle on a side-menu page route; drives the menu and the guard. */
interface SideNavPageHandle {
  /** Menu label; matches the page title. */
  label: string;
  /** Menu group the item sits in. Absent: an unlabelled group. */
  group?: string;
  /** Hides the item and guards the route. Absent: every signed-in user. */
  requires?: "admin";
}

/** One page of a side-menu area, in menu order. */
export interface SideNavPage {
  path: string;
  handle: SideNavPageHandle;
}

/** Props for a side-menu area. */
export interface SideNavLayoutProps {
  /** Area name: the menu heading. */
  title: string;
  /** Every page of the area, in menu order. Groups follow the same order. */
  pages: SideNavPage[];
}

/**
 * An area's side menu beside the current page. Every item is a route, so
 * selection lives in the URL. Hides what the user's roles don't allow, and a
 * group left empty; the route guards and the server still enforce it.
 */
export function SideNavLayout({ title: areaTitle, pages }: SideNavLayoutProps) {
  const { data: permissions } = useCurrentUserPermissions();
  const isAdmin = permissions?.is_admin ?? false;

  const visible = pages.filter(
    (entry) => entry.handle.requires !== "admin" || isAdmin
  );
  const groups = [...new Set(visible.map((entry) => entry.handle.group))].map(
    (label) => ({
      label,
      items: visible.filter((entry) => entry.handle.group === label)
    })
  );

  return (
    <Sheet>
      <div className={layout}>
        <nav className={nav}>
          <p className={title}>{areaTitle}</p>
          {groups.map((entry) => (
            <div className={group} key={entry.label ?? ""}>
              {entry.label && <p className={groupLabel}>{entry.label}</p>}
              {entry.items.map((link) => (
                <NavLink className={item} key={link.path} to={link.path}>
                  {link.handle.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className={content}>
          <Outlet />
        </div>
      </div>
    </Sheet>
  );
}
